import {
  InstancedMesh,
  BoxGeometry,
  EdgesGeometry,
  Matrix4,
  Quaternion,
  Euler,
  Vector3,
} from 'three';
import type { Material, Intersection } from 'three';
import type { Cuboid } from '../../domain/entities/Cuboid';

const _matrix = new Matrix4();
const _position = new Vector3();
const _quaternion = new Quaternion();
const _euler = new Euler();
const _scale = new Vector3();

let sharedBoxGeometry: BoxGeometry | null = null;
let sharedEdgesGeometry: EdgesGeometry | null = null;

function getBoxGeometry(): BoxGeometry {
  if (!sharedBoxGeometry) {
    sharedBoxGeometry = new BoxGeometry(1, 1, 1);
  }
  return sharedBoxGeometry;
}

function getEdgesGeometry(): EdgesGeometry {
  if (!sharedEdgesGeometry) {
    sharedEdgesGeometry = new EdgesGeometry(getBoxGeometry());
  }
  return sharedEdgesGeometry;
}

function setInstanceMatrix(mesh: InstancedMesh, index: number, cuboid: Cuboid): void {
  _position.set(cuboid.position.x, cuboid.position.y, cuboid.position.z);
  _euler.set(0, 0, cuboid.transform.yaw);
  _quaternion.setFromEuler(_euler);
  _scale.set(cuboid.dimensions.width, cuboid.dimensions.height, cuboid.dimensions.depth);

  _matrix.compose(_position, _quaternion, _scale);
  mesh.setMatrixAt(index, _matrix);
}

export function buildFaceInstances(
  cuboids: Cuboid[],
  material: Material,
): InstancedMesh {
  const mesh = new InstancedMesh(getBoxGeometry(), material, cuboids.length);
  updateInstanceMatrices(mesh, cuboids);
  return mesh;
}

export function buildEdgeInstances(
  cuboids: Cuboid[],
  material: Material,
): InstancedMesh {
  const mesh = new InstancedMesh(getEdgesGeometry(), material, cuboids.length);
  updateInstanceMatrices(mesh, cuboids);
  return mesh;
}

export function updateInstanceMatrices(
  mesh: InstancedMesh,
  cuboids: Cuboid[],
): void {
  for (let i = 0; i < cuboids.length; i++) {
    setInstanceMatrix(mesh, i, cuboids[i]);
  }
  mesh.instanceMatrix.needsUpdate = true;
  mesh.count = cuboids.length;
}

export function getInstanceUuid(
  intersection: Intersection,
  cuboids: Cuboid[],
): string | null {
  const id = intersection.instanceId;
  if (id === undefined || id < 0 || id >= cuboids.length) return null;
  return cuboids[id].uuid;
}

export function disposeSharedGeometries(): void {
  sharedBoxGeometry?.dispose();
  sharedEdgesGeometry?.dispose();
  sharedBoxGeometry = null;
  sharedEdgesGeometry = null;
}
